<!--
DataTable helper

### props:
  itemController:     name controller for get/set data
  itemIdentifyField:  filed to display in title dialogs
  headers:            data for help display data, see additional information below
  options:            sync data with parent table data
  selectedItems:      sync data with parent selected items
  footerProps:        footer props fo table
  fixedHeader:        flag fixed header
  showSelect:         flag show select rows
  getDataHook:        callback after get data
  addItemHook:        callback after add item

example:
    <data-source-table
      itemController="microsite"
      itemIdentifyField="title"
      :headers="headers"
      :options.sync="options"
      :selected-items.sync="selectedItems"
      :get-data-hook="getDataHook"
      :add-item-hook="addItemHook"
      ref="dataSourceRef"
    />

use ref="dataSourceRef" for access to helper functions

### template slots:
  <template v-slot:filter_'FIELD_VALUE_IN_HEADER'> - replace default table filter
  <template v-slot:item.'FIELD_VALUE_IN_HEADER'="{ item }"> - replace default table row
  <template v-slot:additional.item.'FIELD_VALUE_IN_HEADER'="{ item }"> - add additional template in text or select editor field

### headers array props:
  text: required, header name
  value: required, filed name in data from server
  filter: object for generate filter, if not set generate text filter
    type: select,multipleSelect,disable - filter type
    items: array list
    itemText: field for select filter object
    itemValue: field for select filter object
  edit: object for generate editable row, if not set just display value
    type: text,number,select,switch,textarea,html - edit type
    items: array list if set select type
    icon: icon for display textarea,html edit
    emptyPlaceholder: show red 'empty' if value is empty
    itemText: field for select edit object
    itemValue: field for select edit object
    updateAllFields: send all fields, by default send only edited field

example:
  {
    text: 'priority',
    value: 'priority',
    filter: { type: 'multipleSelect', items: prioritiesFilter },
    edit: { type: 'select', items: priorities },
  },

TODO:
  - check update clonedItem and switch if header.value is relation variable (like geotag.country)
  - think about add EditableList to edit type and change add_geotag/remove_geotag functions
-->
<template>
  <v-flex xs12>
    <v-data-table
      :options.sync="internalOptions"
      :footer-props="footerProps"
      :headers="computedHeaders"
      :items="items"
      :fixed-header="fixedHeader"
      v-resize="debounceResize"
      :height="tableHeight"
      :show-select="showSelect"
      v-model="dataSelectedItems"
      :server-items-length="dataTotalItems"
      :loading="loading"
      loading-text="Loading... Please wait"
      ref="refDataTable"
    >
      <template v-slot:top>
        <slot name="top"/>

        <confirm-dialog
          :is-open.sync="dialogs.textareaDialog.isOpened"
          width="400"
          confirm-button-text="Save"
          :enter-to-confirm="false"
          @cancel="closeDialog('textareaDialog')"
          @confirm="updateItemField(
              dialogs.textareaDialog.data.field,
              dialogs.textareaDialog.data.text,
              dialogs.textareaDialog.data.item,
            ).finally(() => { closeDialog('textareaDialog') })"
        >
          <template v-slot:title>
            Edit
            {{ getObjectValueByPath(dialogs.textareaDialog.data.item, itemIdentifyField) }}
            {{ dialogs.textareaDialog.data.field }}
          </template>
          <template v-slot:body>
            <v-textarea
              v-model="dialogs.textareaDialog.data.text"
              :label=dialogs.textareaDialog.data.field
              outlined
              autofocus
            />
          </template>
        </confirm-dialog>
        <confirm-dialog
          :is-open.sync="dialogs.htmlDialog.isOpened"
          width="400"
          confirm-button-text="Save"
          :enter-to-confirm="false"
          @cancel="closeDialog('htmlDialog')"
          @confirm="saveHtmlDialog"
        >
          <template v-slot:title>
            Edit
            {{ getObjectValueByPath(dialogs.htmlDialog.data.item, itemIdentifyField) }}
            {{ dialogs.htmlDialog.data.field }}
          </template>
          <template v-slot:body>
            <editor />
          </template>
        </confirm-dialog>

        <confirm-dialog
            :is-open.sync="showSelectColumns"
            dialog-max-width="800"
            @confirm="saveSelectedColumns"
            confirm-button-text="Update"
        >
          <template v-slot:title>
            Select columns
          </template>
          <template v-slot:body>
            <v-row>
              <v-col
                  v-for="header in headers"
                  :key="header.value"
                  cols="3"
              >
                <v-checkbox
                    v-model="selectedColumns"
                    :label="header.text ? header.text : header.value"
                    :value="header.value"
                    :disabled="header.isRequired"
                ></v-checkbox>
              </v-col>
            </v-row>
          </template>
        </confirm-dialog>
      </template>

      <template v-slot:body.prepend>
        <tr class="filter-row">
          <td>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  :outlined="!isEmptyFilter()"
                  @click="clearFilters()"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon color="grey" >
                    mdi-refresh
                  </v-icon>
                </v-btn>
              </template>
              reset filters
            </v-tooltip>
          </td>
          <td v-for="(header, key) in computedHeaders" :key="key">
            <slot :name="`filter_${header.value}`">
              <div v-if="['actions', 'deleteItem'].includes(header.value)"></div>
              <!-- disable -->
              <div v-else-if="header.filter && header.filter.type === 'disable'"></div>
              <!-- select -->
              <v-autocomplete
                v-else-if="header.filter && header.filter.type === 'select'"
                :value="internalOptions.filters[header.value] !== undefined
                  ? internalOptions.filters[header.value] : ''"
                @change="(v) => updateFilter(header.value, v)"
                :items="header.filter.items"
                :item-text="header.filter.itemText ? header.filter.itemText : 'name'"
                :item-value="header.filter.itemValue ? header.filter.itemValue : 'id'"
                :label="header.text"
                :filter="nonDiacriticsSensitiveFilter"
                clearable
                dense
              />
              <!-- multiple select -->
              <v-autocomplete
                v-else-if="header.filter && header.filter.type === 'multipleSelect'"
                :value="internalOptions.filters[header.value] !== undefined
                  ? internalOptions.filters[header.value] : ''"
                @change="(v) => updateFilter(header.value, v)"
                :items="header.filter.items"
                :item-text="header.filter.itemText ? header.filter.itemText : 'name'"
                :item-value="header.filter.itemValue ? header.filter.itemValue : 'id'"
                :label="header.text"
                :filter="nonDiacriticsSensitiveFilter"
                clearable
                multiple
                dense
                chips
                small-chips
                deletable-chips
                :menu-props="{ closeOnContentClick: true }"
              />
              <!-- default -->
              <v-text-field v-else
                :value="internalOptions.filters[header.value] !== undefined
                  ? internalOptions.filters[header.value] : ''"
                @change="(v) => updateFilter(header.value, v)"
                type="text"
                :label="header.text"
                dense
                clearable
                @click:clear="clearFilters(header.value)"
                @keyup.enter="updateFilter(header.value, $event.target.value)"
              />
            </slot>

          </td>
        </tr>
        <slot name="body.prepend"/>
      </template>

      <template v-for="header in computedHeaders" v-slot:[`item.${header.value}`]="{item}">
        <slot :name="`item.${header.value}`" :item="item">
          <!-- deleteItem -->
          <template v-if="header.value === 'deleteItem'">
            <v-btn
              v-if="!hasSomeAbilities($constants.ABILITY.INTERN)"
              icon @click="remove(item)"
            >
              <v-icon color="red">mdi-delete</v-icon>
            </v-btn>
          </template>
          <!-- edit text -->
          <template v-else-if="header.edit && ['text', 'number'].includes(header.edit.type)">
            <v-edit-dialog
              @save="header.edit.updateAllFields
                 ? updateItem(clonedItem, item)
                 : updateItemField(header.value, clonedItem[header.value], item)"
              @open="clonedItem = {...item}"
            >

              {{ getObjectValueByPath(item, header.value) }}

              <div
                v-if="header.edit.emptyPlaceholder && !getObjectValueByPath(item, header.value)"
                class="font-italic font-weight-thin red--text" v-text="'empty'"
              />

              <slot :name="`additional.item.${header.value}`" :item="item"/>

              <template v-slot:input>
                <v-text-field
                  v-model="clonedItem[header.value]"
                  label="Edit"
                  single-line
                  counter
                  :type="header.edit.type"
                />
              </template>
            </v-edit-dialog>
          </template>
          <!-- edit select -->
          <template v-else-if="header.edit && header.edit.type === 'select'">
            <v-edit-dialog
              @save="header.edit.updateAllFields
                 ? updateItem(clonedItem, item)
                 : updateItemField(header.value, clonedItem[header.value], item)"
              @open="clonedItem = {...item}"
            >
              {{ showSelectValue(item, header) }}

              <div
                v-if="header.edit.emptyPlaceholder && !getObjectValueByPath(item, header.value)"
                class="font-italic font-weight-thin red--text" v-text="'empty'"
              />

              <slot :name="`additional.item.${header.value}`" :item="item"/>

              <template v-slot:input>
                <v-select
                  v-model="clonedItem[header.value]"
                  :items="header.edit.items"
                  :item-text="header.edit.itemText ? header.edit.itemText : 'text'"
                  :item-value="header.edit.itemValue ? header.edit.itemValue : 'value'"
                  :clearable="!!header.edit.clearable"
                ></v-select>
              </template>
            </v-edit-dialog>
          </template>
          <!-- edit switch -->
          <template v-else-if="header.edit && header.edit.type === 'switch'">
            <v-switch
              class="mt-3"
              dense
              hide-details
              v-model="item[header.value]"
              @change="(v) => switchChange(item, header.value, v, header.edit.updateAllFields)"
            />
          </template>
          <!-- edit textarea -->
          <template v-else-if="header.edit && header.edit.type === 'textarea'">
            <v-btn icon small
              @click="openDialog(
                'textareaDialog',
                 { text: getObjectValueByPath(item, header.value), field: header.value, item: item}
                )"
            >
              <v-icon :color="(getObjectValueByPath(item, header.value)) ? 'cyan' : 'grey'" dense>
                {{ header.edit.icon ? header.edit.icon : 'mdi-notebook-edit-outline' }}
              </v-icon>
            </v-btn>
          </template>
          <!-- edit htmlDialog -->
          <template v-else-if="header.edit && header.edit.type === 'html'">
            <v-btn icon small
              @click="openHtmlDialog(item, header.value)"
            >
              <v-icon :color="(getObjectValueByPath(item, header.value)) ? 'cyan' : 'grey'" dense>
                {{ header.edit.icon ? header.edit.icon : 'mdi-notebook-edit-outline' }}
              </v-icon>
            </v-btn>
          </template>
          <!-- default -->
          <template v-else>
            {{ getObjectValueByPath(item, header.value) }}
          </template>
        </slot>
      </template>
    </v-data-table>
  </v-flex>
</template>

<script>

import debounce from 'lodash.debounce';
import { deepEqual, getObjectValueByPath, isObject } from 'vuetify/lib/util/helpers';
import { mapActions } from 'vuex';
import manageDialogsMixin from '../../mixins/manageDialogsMixin';
import ConfirmDialog from '../dialogs/ConfirmDialog.vue';
import Editor from '../Editor.vue';

const DEFAULT_ITEMS_PER_PAGE = 50;

export default {
  name: 'DataSourceTable',
  components: {
    Editor,
    ConfirmDialog,
  },
  props: {
    storagePrefix: {
      type: String,
      default: '',
    },
    itemController: {
      type: String,
      default: '',
    },
    itemIdentifyField: {
      type: String,
      default: 'id',
    },
    headers: {
      type: Array,
      default: () => [],
    },
    options: {
      type: Object,
    },
    selectedItems: {
      type: Array,
      default: () => [],
    },
    footerProps: {
      type: Object,
      default: () => ({
        itemsPerPageOptions: [10, 25, 50, 100, 200, 300, -1],
      }),
    },
    fixedHeader: {
      type: Boolean,
      default: true,
    },
    showSelect: {
      type: Boolean,
      default: true,
    },
    getDataHook: {
      type: Function,
    },
    addItemHook: {
      type: Function,
    },
  },

  mixins: [
    manageDialogsMixin,
  ],

  data: () => ({
    loading: true,
    tableHeight: null,
    dataSelectedItems: [],
    showSelectColumns: false,
    selectedColumns: [],
    dataTotalItems: 0,
    internalOptions: {
      page: 1,
      itemsPerPage: DEFAULT_ITEMS_PER_PAGE,
      sortBy: ['id'],
      sortDesc: [false],
      mustSort: true,
      multiSort: false,
      filters: {},
    },
    initFilters: {},
    items: [],
    clonedItem: {},
    dialogs: {
      textareaDialog: {
        isOpened: false,
        data: {
          text: '',
          field: '',
          item: {},
        },
        defaults: {
          text: '',
          field: '',
          item: {},
        },
      },
      htmlDialog: {
        isOpened: false,
        data: {
          text: '',
          field: '',
          item: {},
        },
        defaults: {
          text: '',
          field: '',
          item: {},
        },
      },
    },
  }),

  created() {
    this.initFilters = { ...(this.options.filters ? this.options.filters : {}) };
    this.localReadTableOptions();

    // add id from GET param
    if (this.$route.query.ids) {
      // hook get new filters with save default
      const filters = { ...this.initFilters, ...{ id: this.$route.query.ids } };
      this.updateInternalOptions({ filters });
    }

    // add default columns list
    if (!this.internalOptions.columns) {
      this.internalOptions.columns = this.headers
        .filter((el) => !Object.hasOwn(el, 'isDefault') || el.isDefault)
        .map((el) => el.value);
    }
    this.selectedColumns = this.internalOptions.columns;
  },

  mounted() {
  },

  methods: {
    getObjectValueByPath,
    ...mapActions({
      setSnack: 'snackbar/setState',
    }),

    onResize() {
      const pagePadding = 12;
      const pageFooter = 36;
      let tableHeight = window.innerHeight - pageFooter - pagePadding;

      const tableHeader = this.$refs.refDataTable.$el.getElementsByTagName('header').item(0);
      if (tableHeader) {
        const sizeTableHeader = tableHeader.getBoundingClientRect();

        tableHeight -= sizeTableHeader.top;
        tableHeight -= sizeTableHeader.height;
      }
      const tableFooter = this.$refs.refDataTable.$el.getElementsByClassName('v-data-footer').item(0);
      if (tableFooter) {
        tableHeight -= tableFooter.offsetHeight;
      }

      this.tableHeight = tableHeight;
    },

    getApiData() {
      this.clearSelectedItems();
      this.loading = true;

      this.$requestHelpers
        .post(`api/admin/${this.itemController}/filteredAndPaginated`,
          this.internalOptions)
        .then((response) => {
          if (response.data.data) {
            this.items = response.data.data;
            this.dataTotalItems = response.data.total;
            this.internalOptions.page = response.data.current_page > response.data.last_page
              ? response.data.last_page
              : response.data.current_page;
          }
          this.loading = false;

          if (typeof this.getDataHook === 'function') {
            this.getDataHook();
          }
        });
    },

    // add new
    saveNew(data) {
      return this.$requestHelpers.post(
        `api/admin/${this.itemController}`,
        data,
      )
        .then((response) => {
          this.addItem(response.data.result);
        });
    },
    addItem(item) {
      this.items.unshift(item);
      this.dataTotalItems += 1;
      if (typeof this.addItemHook === 'function') {
        this.addItemHook(item);
      }
    },

    // update
    updateItem(itemClone, item) {
      return this.$requestHelpers
        .patch(`api/admin/${this.itemController}/${itemClone.id}`, itemClone)
        .then((response) => {
          if (response.data.success) {
            this.setSnack({
              color: 'green',
              text: response.data.message ? response.data.message : 'saved',
            });
          }

          this.updateData(response.data.result);
        }).catch((response) => {
          if (response?.data?.result?.id) {
            this.updateData(response.data.result);
          } else {
            this.updateData({ ...item });
          }
        });
    },
    updateItemField(field, value, item) {
      const itemClone = {
        id: item.id,
        [field]: value,
      };
      return this.updateItem(itemClone, item);
    },
    updateBulk(itemIds, itemFields) {
      return this.$requestHelpers
        .post(`api/admin/${this.itemController}/updateBulk`, { ids: itemIds, itemFields })
        .then((response) => {
          if (response.data.success) {
            let commonStatus = true;
            for (let i = 0; i < response.data.result.statuses.length; i += 1) {
              const { status } = response.data.result.statuses[i];

              commonStatus &&= status;

              if (status) {
                this.updateData(response.data.result.items[i]);
              }
            }

            if (commonStatus) {
              this.setSnack({
                color: 'green',
                text: response.data.message ? response.data.message : 'saved',
              });
            } else {
              this.setSnack({
                color: 'orange',
                text: response.data.message,
              });
            }
          }
        }).finally(() => {
          this.clearSelectedItems();
        });
    },
    updateData(data) {
      const index = this.items.findIndex((e) => e.id === data.id);

      Object.assign(this.items[index], data);
    },
    switchChange(item, field, newValue, updateAllFields) {
      const oldItem = { ...item };
      oldItem[field] = !newValue;

      return updateAllFields
        ? this.updateItem(item, oldItem)
        : this.updateItemField(field, item[field], oldItem);
    },

    clearSelectedItems() {
      this.dataSelectedItems = [];
    },

    // remove
    remove(item) {
      this.$confirm(
        `Are you sure you want to delete ${this.itemController} ${getObjectValueByPath(item, this.itemIdentifyField)}?`,
      ).then((res) => {
        if (!res) return;

        this.$requestHelpers
          .delete(`api/admin/${this.itemController}/${item.id}`)
          .then(() => {
            this.removeItem(item);
          });
      });
    },
    removeItem(item) {
      this.items = this.items.filter((e) => e.id !== item.id);
      this.dataTotalItems -= 1;
    },
    removeBulk(itemIds) {
      return this.$requestHelpers
        .post(`api/admin/${this.itemController}/deleteBulk`,
          {
            ids: itemIds,
          })
        .then((response) => {
          if (response.data.success) {
            let commonStatus = true;
            for (let i = 0; i < response.data.result.statuses.length; i += 1) {
              const { status } = response.data.result.statuses[i];
              commonStatus &&= status;
            }

            if (commonStatus) {
              this.setSnack({
                color: 'green',
                text: response.data.message ? response.data.message : 'deleted',
              });
            } else {
              this.setSnack({
                color: 'orange',
                text: response.data.message,
              });
            }

            this.items = this.items.filter((e) => !response.data.result.ids.includes(e.id));
            // eslint-disable-next-line max-len
            this.dataSelectedItems = this.dataSelectedItems.filter((e) => !response.data.result.ids.includes(e.id));
          }
        });
    },

    // columns
    openDialogSelectColumns() {
      this.showSelectColumns = true;
    },
    saveSelectedColumns() {
      this.internalOptions.columns = this.selectedColumns;
      this.showSelectColumns = false;
    },

    // filters
    updateFilter(filterName, value) {
      if ((Array.isArray(value) && value.length > 0)
        || (!Array.isArray(value) && (typeof value === 'number' || value))) {
        this.$set(this.internalOptions.filters, filterName, value);
      } else {
        this.$delete(this.internalOptions.filters, filterName);
      }
    },
    isEmptyFilter() {
      return JSON.stringify(this.initFilters) === JSON.stringify(this.internalOptions.filters);
    },
    clearFilters(filterName = '') {
      if (filterName) {
        this.$delete(this.internalOptions.filters, filterName);
      } else {
        this.internalOptions.filters = { ...this.initFilters };
      }
    },
    removeEmptyArrayFilters() {
      let hasChanges = false;

      Object.keys(this.internalOptions.filters).forEach((key) => {
        if (Array.isArray(this.internalOptions.filters[key])
          && this.internalOptions.filters[key].length === 0) {
          this.$delete(this.internalOptions.filters, key);
          hasChanges = true;
        }
      });

      return hasChanges;
    },
    resetItemsPerPageIfAllAndNoFilters() {
      if (this.internalOptions.itemsPerPage === -1 && this.isEmptyFilter()) {
        this.internalOptions.itemsPerPage = DEFAULT_ITEMS_PER_PAGE;
        return true;
      }

      return false;
    },
    normalizeInternalOptions() {
      const hasEmptyArrayFilters = this.removeEmptyArrayFilters();
      const hasItemsPerPageReset = this.resetItemsPerPageIfAllAndNoFilters();

      return hasEmptyArrayFilters || hasItemsPerPageReset;
    },
    requestFilters() {
      this.localSaveTableOptions();
      this.getApiData();
    },
    localReadTableOptions() {
      let storageData = {};
      if (localStorage[`table_options_${this.storagePrefix}${this.itemController}`]) {
        storageData = JSON.parse(localStorage[`table_options_${this.storagePrefix}${this.itemController}`]);
      }
      this.updateInternalOptions(storageData);
    },
    localSaveTableOptions() {
      localStorage[`table_options_${this.storagePrefix}${this.itemController}`] = JSON.stringify(this.internalOptions);
    },
    updateInternalOptions(data = {}) {
      this.internalOptions = {
        ...{}, ...this.internalOptions, ...this.options, ...data,
      };
    },

    allItems() {
      return this.items;
    },
    openHtmlDialog(item, headerValue) {
      this.$store.commit('richeditor/setText', getObjectValueByPath(item, headerValue));
      this.openDialog(
        'htmlDialog',
        { text: getObjectValueByPath(item, headerValue), field: headerValue, item },
      );
    },
    saveHtmlDialog() {
      let { text } = this.$store.state.richeditor;

      // clear "empty" notes
      let textTrimmed = text.trim();
      textTrimmed = textTrimmed.replace(/<p>(\s*)<\/p>/, '');
      text = textTrimmed === '<p></p>' ? '' : textTrimmed;

      this.updateItemField(
        this.dialogs.htmlDialog.data.field,
        text,
        this.dialogs.htmlDialog.data.item,
      ).finally(() => { this.closeDialog('htmlDialog'); });
    },

    showSelectValue(item, header) {
      let value = getObjectValueByPath(item, header.value);

      // search value by items value
      if (header.edit.items && isObject(header.edit.items[0])) {
        const key = header.edit.itemText ? header.edit.itemText : 'text';
        const val = header.edit.itemValue ? header.edit.itemValue : 'value';

        const fountItem = header.edit.items.find((listItem) => listItem[val] === value);
        if (fountItem) {
          value = fountItem[key];
        }
      }

      return value;
    },
  },

  computed: {
    debounceResize() {
      return debounce(this.onResize, 100);
    },

    computedHeaders() {
      return this.headers.filter((el) => this.internalOptions.columns.includes(el.value));
    },

    computedOptions() {
      return JSON.stringify(this.internalOptions);
    },
  },

  watch: {
    internalOptions: {
      handler() {
        this.internalOptions.isEmptyFilter = this.isEmptyFilter();
      },
      deep: true,
    },
    computedOptions: {
      handler(options, old) {
        if (deepEqual(options, old)) return;
        if (this.normalizeInternalOptions()) return;
        this.$emit('update:options', this.internalOptions);
        this.requestFilters();
      },
      deep: true,
    },
    dataSelectedItems: {
      handler() {
        this.$emit('update:selectedItems', this.dataSelectedItems);
      },
    },
  },
};
</script>

<style scoped>
.filter-row td{
  height: 70px !important;
  min-height: 70px;
}
</style>
